import type { ReactNode } from 'react';
import LoadSpinner from 'svg/LoadSpinner';
import Button from 'components/Button';
import classNames from 'styles/utils/classNames';

interface Props {
  icon: ReactNode;
  /** Required: these are icon-only, so the label is the only accessible name. */
  label: string;
  onClick?: () => void;
  /** Swaps the icon for a spinner — these are icon-only, so there's nowhere else to show progress. */
  isLoading?: boolean;
  className?: string;
}

/**
 * Figma's "Round/Floating/White" — the 32px white disc used for every icon-only
 * action on this page (back, camera, section edit). It stays white in dark mode by
 * design: several instances float over the cover photo, where the surrounding
 * colour is the image rather than the page.
 */
export default function RoundIconButton({
  icon,
  label,
  onClick,
  isLoading = false,
  className,
}: Props) {
  return (
    <Button
      variant="secondary"
      size="icon"
      aria-label={label}
      onClick={onClick}
      disabled={isLoading}
      className={classNames(
        'h-8 w-8 shrink-0 border-transparent bg-color-bg-lightmode-primary text-color-text-lightmode-primary shadow-fab',
        className,
      )}
    >
      {isLoading ? <LoadSpinner className="h-4 w-4 animate-spin" /> : icon}
    </Button>
  );
}
